/**
 * Add to Home Screen Prompt for iOS Users
 *
 * iOS Safari requires users to add the PWA to their home screen
 * before they can receive push notifications. This component
 * detects iOS users and shows them instructions.
 *
 * Requirements.md: E.3 - Platform-specific notification handling
 */

import { useState, useEffect } from 'react';
import { X, Share } from 'lucide-react';

export function AddToHomeScreenPrompt() {
  const [showPrompt, setShowPrompt] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isInStandaloneMode, setIsInStandaloneMode] = useState(false);

  useEffect(() => {
    // Detect iOS
    const userAgent = window.navigator.userAgent.toLowerCase();
    const platform = (navigator as any).platform?.toLowerCase() || '';

    // Detect iOS (iPhone, iPad, iPod)
    const isIOSDevice =
      /iphone|ipad|ipod/.test(userAgent) ||
      (platform === 'macintel' && navigator.maxTouchPoints > 1); // iPad on iOS 13+

    // Check if already in standalone mode (added to home screen)
    const isStandalone =
      window.matchMedia('(display-mode: standalone)').matches ||
      (window.navigator as any).standalone === true;

    setIsIOS(isIOSDevice);
    setIsInStandaloneMode(isStandalone);

    // Show prompt if iOS and not in standalone mode and not dismissed
    const dismissed = localStorage.getItem('a2hs-prompt-dismissed');
    const dismissedTime = localStorage.getItem('a2hs-prompt-dismissed-time');

    // Reset dismissal after 7 days
    if (dismissed && dismissedTime) {
      const daysSinceDismissed = (Date.now() - parseInt(dismissedTime)) / (1000 * 60 * 60 * 24);
      if (daysSinceDismissed > 7) {
        localStorage.removeItem('a2hs-prompt-dismissed');
        localStorage.removeItem('a2hs-prompt-dismissed-time');
      }
    }

    if (isIOSDevice && !isStandalone && !localStorage.getItem('a2hs-prompt-dismissed')) {
      // Show after 3 seconds to avoid overwhelming the user
      const timer = setTimeout(() => setShowPrompt(true), 3000);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleDismiss = () => {
    setShowPrompt(false);
    localStorage.setItem('a2hs-prompt-dismissed', 'true');
    localStorage.setItem('a2hs-prompt-dismissed-time', Date.now().toString());
  };

  const handleRemindLater = () => {
    setShowPrompt(false);
    // Don't set dismissed flag, so it shows again on next visit
  };

  if (!showPrompt || !isIOS || isInStandaloneMode) {
    return null;
  }

  return (
    <div className="fixed bottom-[calc(5.5rem+env(safe-area-inset-bottom))] left-0 right-0 mx-3 sm:mx-4 bg-gradient-to-r from-blue-600 to-blue-700 text-white p-4 shadow-2xl z-[60] animate-slide-up border-t-4 border-blue-400 rounded-2xl max-h-[55vh] overflow-y-auto">
      <div className="max-w-2xl mx-auto">
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 mt-1">
            <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
              <Share size={20} className="text-white" />
            </div>
          </div>

          <div className="flex-1 min-w-0">
            <h3 className="font-bold text-lg mb-1 flex items-center gap-2">
              📱 Enable Push Notifications
            </h3>
            <p className="text-sm mb-3 text-blue-100">
              To receive water reminders on your iPhone/iPad, add SWYG to your Home Screen:
            </p>

            <div className="bg-white/10 rounded-lg p-3 mb-3">
              <ol className="text-sm space-y-2 list-decimal list-inside">
                <li className="flex items-start gap-2">
                  <span className="flex-shrink-0 mt-0.5">1.</span>
                  <span>
                    Tap the <strong>Share button</strong>{' '}
                    <span className="inline-flex items-center justify-center w-6 h-6 bg-white/20 rounded text-xs">
                      ⎙
                    </span>{' '}
                    in Safari (bottom of screen)
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="flex-shrink-0 mt-0.5">2.</span>
                  <span>
                    Scroll down and tap <strong>"Add to Home Screen"</strong>
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="flex-shrink-0 mt-0.5">3.</span>
                  <span>
                    Tap <strong>"Add"</strong> to confirm
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="flex-shrink-0 mt-0.5">4.</span>
                  <span>
                    Open <strong>SWYG</strong> from your Home Screen
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="flex-shrink-0 mt-0.5">5.</span>
                  <span>Allow notifications when prompted</span>
                </li>
              </ol>
            </div>

            <div className="flex gap-2">
              <button
                onClick={handleRemindLater}
                className="px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg text-sm font-medium transition-colors"
              >
                Remind Me Later
              </button>
              <button
                onClick={handleDismiss}
                className="px-4 py-2 bg-white/10 hover:bg-white/20 rounded-lg text-sm font-medium transition-colors"
              >
                Don't Show Again
              </button>
            </div>
          </div>

          <button
            onClick={handleDismiss}
            className="flex-shrink-0 text-white/80 hover:text-white transition-colors p-1"
            aria-label="Dismiss"
          >
            <X size={24} />
          </button>
        </div>
      </div>
    </div>
  );
}

// Add animation styles to your global CSS or tailwind.config.js:
// @keyframes slide-up {
//   from {
//     transform: translateY(100%);
//     opacity: 0;
//   }
//   to {
//     transform: translateY(0);
//     opacity: 1;
//   }
// }
// .animate-slide-up {
//   animation: slide-up 0.3s ease-out;
// }
